import apiClient from './apiClient';

// OpenAPI-compliant interfaces aligned with API specification
export interface YamlConfig {
  id: number;
  uuid: string;
  platform: number;
  name: string;
  description?: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface YamlConfigDetail extends YamlConfig {
  config: string; // Full YAML configuration content
}

export interface YamlConfigCreate {
  platform: number;
  name: string;
  description?: string;
  config: string;
  is_active?: boolean;
  is_default?: boolean;
}

// Legacy alias for backward compatibility
export interface CreateYamlConfigRequest extends YamlConfigCreate {}

export interface YamlConfigListParams {
  platform?: number;
  is_active?: boolean;
  include_config?: boolean;
}

// SSOT Schema interfaces
export interface GenerateExtractionYamlRequest {
  platform_id: number;
  job_type_id: number;
  data_type?: string;
  endpoint_ids?: number[];
}

export interface GenerateLoadingYamlRequest {
  platform_id: number;
  job_type_id: number;
  target_endpoints?: number[];
}

export interface GenerateYamlResponse {
  yaml_content: string;
  suggested_name: string;
  platform_name?: string;
}

class YamlConfigService {
  async getAllYamlConfigs(params?: YamlConfigListParams): Promise<YamlConfig[]> {
    try {
      const response = await apiClient.get<{ count: number; results: YamlConfig[] } | YamlConfig[]>('/api/yaml-configs/', { params });
      
      // Handle both paginated and direct array responses
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return response.data.results || [];
      }
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch YAML configurations:', error);
      // Return empty array instead of throwing to prevent UI breaks
      return [];
    }
  }

  async getYamlConfig(id: number, includeConfig = false): Promise<YamlConfigDetail> {
    try {
      const response = await apiClient.get<YamlConfigDetail>(`/api/yaml-configs/${id}/`, {
        params: { include_config: includeConfig }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch YAML configuration ${id}:`, error);
      throw this.handleError(error);
    }
  }

  async createYamlConfig(data: YamlConfigCreate): Promise<YamlConfig> {
    try {
      const response = await apiClient.post<YamlConfig>('/api/yaml-configs/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create YAML configuration:', error);
      throw this.handleError(error);
    }
  }

  // SSOT Schema methods
  async generateExtractionYaml(data: GenerateExtractionYamlRequest): Promise<GenerateYamlResponse> {
    try {
      const response = await apiClient.post<GenerateYamlResponse>('/api/ssot/generate-extraction-yaml/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to generate extraction YAML:', error);
      throw this.handleError(error);
    }
  }

  async generateLoadingYaml(data: GenerateLoadingYamlRequest): Promise<GenerateYamlResponse> {
    try {
      const response = await apiClient.post<GenerateYamlResponse>('/api/ssot/generate-loading-yaml/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to generate loading YAML:', error);
      throw this.handleError(error);
    }
  }

  // Utility methods for working with YAML configurations
  async getYamlConfigsByPlatform(platformId: number): Promise<YamlConfig[]> {
    try {
      const allConfigs = await this.getAllYamlConfigs({ platform: platformId });
      return allConfigs;
    } catch (error) {
      console.error(`Failed to fetch YAML configurations for platform ${platformId}:`, error);
      throw error;
    }
  }

  async validateYamlConfig(config: string): Promise<{ valid: boolean; errors?: string[] }> {
    try {
      // This would typically call a validation endpoint
      // For now, we'll do a basic YAML syntax check
      const trimmed = config.trim();
      if (!trimmed) {
        return { valid: false, errors: ['Configuration cannot be empty'] };
      }
      
      // Basic YAML validation - in a real implementation, this would be server-side
      return { valid: true };
    } catch (error) {
      return { valid: false, errors: ['Invalid YAML format'] };
    }
  }

  private handleError(error: any): Error {
    if (error?.response?.data?.error) {
      return new Error(error.response.data.error);
    }
    return error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

export const yamlConfigService = new YamlConfigService();